import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminLogin, fetchOrders, updateOrderStatus, updateOrderPacked, deleteOrder, fetchArchive, restoreOrder, resetOrderNumbers, clearArchive } from "@/lib/api";
import { LogOut, RefreshCw, Trash2, ArrowLeft, Bell, BellOff, FileDown, Archive, Undo2, ArrowDownAZ, Search, PackageCheck, Shirt, Printer, RotateCcw } from "lucide-react";
import { API } from "@/lib/api";
import { MessageList } from "@/components/MessageList";
import { MessageSquare } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "Afventer", className: "bg-amber-100 text-amber-800" },
  paid: { label: "Betalt", className: "bg-blue-100 text-blue-800" },
  delivered: { label: "Udleveret", className: "bg-emerald-100 text-emerald-800" },
};

const SIZE_ORDER = ["4-6", "8-10", "12-14", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];

const printPackingList = (orders) => {
  const sorted = [...orders].sort(
    (a, b) => a.customer_name.toLowerCase().localeCompare(b.customer_name.toLowerCase(), "da")
  );
  const rows = sorted
    .map(
      (o) => `
      <div class="order">
        <div class="head">
          <span class="box">${o.packed ? "☑" : "☐"}</span>
          <strong>${o.customer_name}</strong>
          <span class="meta">#${o.order_number} · ${o.phone} · ${o.total} kr · ${
        o.status === "paid" ? "Betalt" : o.status === "delivered" ? "Udleveret" : "Ikke betalt"
      }</span>
        </div>
        <ul>${o.items
          .map((i) => `<li>${i.product_name} · ${i.size} · ${i.color_label}</li>`)
          .join("")}</ul>
      </div>`
    )
    .join("");
  const html = `<!DOCTYPE html><html><head><title>Pakkeliste – ÅF Trøjer</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{font-size:20px;margin:0 0 4px}
    .sub{color:#64748b;font-size:13px;margin-bottom:20px}
    .order{border-bottom:1px solid #cbd5e1;padding:10px 0;break-inside:avoid}
    .head{display:flex;gap:10px;align-items:baseline;font-size:15px}
    .box{font-size:18px}
    .meta{color:#64748b;font-size:12px}
    ul{margin:4px 0 0 32px;padding:0;font-size:13px;color:#334155}
    li{margin:2px 0}
  </style></head><body>
    <h1>Pakkeliste – ÅF Trøjer</h1>
    <div class="sub">${sorted.length} bestillinger · udskrevet ${new Date().toLocaleString("da-DK")}</div>
    ${rows}
  </body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.focus();
  w.print();
};

const printOrderSheet = (ordersToPrint) => {
  const grandTotal = ordersToPrint.reduce((n, o) => n + o.total, 0);
  const blocks = ordersToPrint
    .map(
      (o) => `
      <div class="order">
        <div class="ordhead">
          <span class="onum">Bestilling #${o.order_number}</span>
          <span class="odate">${new Date(o.created_at).toLocaleString("da-DK")}</span>
        </div>
        <table>
          ${o.items
            .map(
              (i) => `<tr><td>${i.product_name}</td><td>${i.size}</td><td>${i.color_label}</td><td class="r">${i.price} kr</td></tr>`
            )
            .join("")}
          <tr class="tot"><td colspan="3">I alt</td><td class="r">${o.total} kr</td></tr>
        </table>
      </div>`
    )
    .join("");
  const names = [...new Set(ordersToPrint.map((o) => o.customer_name))].join(" / ");
  const phones = [...new Set(ordersToPrint.map((o) => o.phone))].join(" / ");
  const html = `<!DOCTYPE html><html><head><title>Ordre – ${names}</title><style>
    @page{size:A4;margin:20mm}
    body{font-family:Arial,sans-serif;color:#0f172a;padding:24px}
    .brand{font-size:14px;color:#64748b;letter-spacing:2px;text-transform:uppercase;margin-bottom:16px}
    h1{font-size:34px;margin:0 0 4px}
    .phone{font-size:20px;color:#334155;margin-bottom:28px}
    .order{margin-bottom:24px;break-inside:avoid}
    .ordhead{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #0f172a;padding-bottom:6px;margin-bottom:8px}
    .onum{font-size:20px;font-weight:bold}
    .odate{font-size:12px;color:#64748b}
    table{width:100%;border-collapse:collapse;font-size:15px}
    td{padding:8px 6px;border-bottom:1px solid #e2e8f0}
    .r{text-align:right}
    .tot td{font-weight:bold;border-bottom:none;font-size:16px}
    .grand{margin-top:16px;display:flex;justify-content:space-between;font-size:22px;font-weight:bold;border-top:3px solid #0f172a;padding-top:12px}
  </style></head><body>
    <div class="brand">ÅF Trøjer · Ordreseddel</div>
    <h1>${names}</h1>
    <div class="phone">Tlf: ${phones}</div>
    ${blocks}
    ${ordersToPrint.length > 1 ? `<div class="grand"><span>Samlet i alt</span><span>${grandTotal} kr</span></div>` : ""}
  </body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.focus();
  w.print();
};

// Simple Web Audio "ding" – no external file needed
const playDing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.5);
  } catch {
    /* noop */
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("af_admin_token") || "");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [archive, setArchive] = useState([]);
  const [view, setView] = useState("active"); // 'active' | 'archive'
  const [filter, setFilter] = useState("all"); // 'all' | 'pending' | 'paid' | 'delivered'
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'name'
  const [search, setSearch] = useState("");
  const [showSizes, setShowSizes] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [showMessages, setShowMessages] = useState(false);
  const lastSeenIdsRef = useRef(null);

  const loadOrders = async (t, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchOrders(t);

      // Detect new orders
      if (lastSeenIdsRef.current !== null) {
        const known = lastSeenIdsRef.current;
        const newOnes = data.filter((o) => !known.has(o.id));
        if (newOnes.length > 0) {
          if (soundOn) playDing();
          toast.success(
            newOnes.length === 1
              ? `Ny bestilling #${newOnes[0].order_number} fra ${newOnes[0].customer_name}`
              : `${newOnes.length} nye bestillinger`
          );
        }
      }
      lastSeenIdsRef.current = new Set(data.map((o) => o.id));
      setOrders(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken("");
        localStorage.removeItem("af_admin_token");
        toast.error("Sessionen er udløbet");
      } else if (!silent) {
        toast.error("Kunne ikke hente bestillinger");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadArchive = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await fetchArchive(token);
      setArchive(data);
    } catch {
      toast.error("Kunne ikke hente arkivet");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreOrder(token, id);
      toast.success("Bestillingen er gendannet");
      await loadArchive();
      await loadOrders(token, true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gendanne");
    }
  };

  // Initial load + polling
  useEffect(() => {
    if (!token) return;
    lastSeenIdsRef.current = null; // reset so first load doesn't ding
    loadOrders(token);
    const id = setInterval(() => loadOrders(token, true), 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Persist sound preference
  useEffect(() => {
    const saved = localStorage.getItem("af_admin_sound");
    if (saved !== null) setSoundOn(saved === "1");
  }, []);
  useEffect(() => {
    localStorage.setItem("af_admin_sound", soundOn ? "1" : "0");
  }, [soundOn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const t = await adminLogin(password);
      localStorage.setItem("af_admin_token", t);
      setToken(t);
      setPassword("");
    } catch {
      toast.error("Forkert adgangskode");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("af_admin_token");
    setToken("");
    setOrders([]);
    lastSeenIdsRef.current = null;
  };

  const handleStatus = async (id, status) => {
    try {
      await updateOrderStatus(token, id, status);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch {
      toast.error("Kunne ikke opdatere");
    }
  };

  const handlePacked = async (id, packed) => {
    try {
      await updateOrderPacked(token, id, packed);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, packed } : o)));
    } catch {
      toast.error("Kunne ikke opdatere");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Slet denne bestilling?")) return;
    try {
      await deleteOrder(token, id);
      setOrders((prev) => prev.filter((o) => o.id !== id));
      if (lastSeenIdsRef.current) lastSeenIdsRef.current.delete(id);
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center bg-slate-50 px-4 py-8">
        <form
          onSubmit={handleLogin}
          className="bg-white rounded-3xl shadow-sm border-2 border-slate-200 p-6 sm:p-8 w-full max-w-md"
        >
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-1">Admin</h1>
          <p className="text-slate-500 font-semibold mb-6">ÅF Trøjer bestillinger</p>
          <input
            data-testid="input-admin-password"
            type="password"
            placeholder="Adgangskode"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="h-14 w-full px-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-base font-semibold focus:border-slate-900 outline-none mb-4 min-w-0"
          />
          <button
            data-testid="btn-admin-login"
            type="submit"
            className="h-14 w-full bg-slate-900 text-white rounded-xl font-display text-lg font-bold active:scale-95 hover:bg-slate-800 transition-all"
          >
            Log ind
          </button>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-slate-400 hover:text-slate-700"
          >
            <ArrowLeft size={14} /> Tilbage til butik
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen w-screen flex flex-col bg-slate-50 lg:overflow-hidden">
      {/* Header */}
      <header className="px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-white border-b-2 border-slate-200">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
            {view === "active" ? "Bestillinger" : "Arkiv (alle ordrer)"}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 font-semibold">
            {view === "active"
              ? `${orders.length} ${orders.length === 1 ? "bestilling" : "bestillinger"}`
              : `${archive.length} ${archive.length === 1 ? "ordre" : "ordrer"} i alt (også slettede)`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="btn-toggle-view"
            onClick={() => {
              if (view === "active") {
                setView("archive");
                loadArchive();
              } else {
                setView("active");
              }
            }}
            className={`h-11 px-3 sm:px-4 rounded-xl font-bold transition-all active:scale-95 flex items-center gap-2 ${
              view === "archive"
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : "bg-violet-100 text-violet-700 hover:bg-violet-200"
            }`}
          >
            <Archive size={16} />
            <span className="hidden sm:inline">{view === "archive" ? "Aktive" : "Arkiv"}</span>
          </button>
          <button
            data-testid="btn-open-messages"
            onClick={() => setShowMessages(true)}
            className="h-11 px-3 sm:px-4 rounded-xl bg-sky-100 text-sky-700 font-bold hover:bg-sky-200 active:scale-95 transition-all flex items-center gap-2"
          >
            <MessageSquare size={16} />
            <span className="hidden sm:inline">Beskeder</span>
          </button>
          <button
            data-testid="btn-export-excel"
            onClick={() => window.open(`${API}/admin/export.xlsx?token=${encodeURIComponent(token)}`, "_blank")}
            className="h-11 px-3 sm:px-4 rounded-xl bg-emerald-100 text-emerald-700 font-bold hover:bg-emerald-200 active:scale-95 transition-all flex items-center gap-2"
          >
            <FileDown size={16} />
            <span className="hidden sm:inline">Excel</span>
          </button>
          {view === "archive" && (
            <button
              data-testid="btn-clear-archive"
              onClick={async () => {
                if (!window.confirm("Slet ALLE ordrer i arkivet permanent? Dette kan ikke fortrydes.")) return;
                try {
                  await clearArchive(token);
                  setArchive([]);
                  toast.success("Arkivet er ryddet");
                } catch {
                  toast.error("Kunne ikke rydde arkivet");
                }
              }}
              className="h-11 px-3 sm:px-4 rounded-xl bg-red-100 text-red-700 font-bold hover:bg-red-200 active:scale-95 transition-all flex items-center gap-2"
            >
              <Trash2 size={16} />
              <span className="hidden sm:inline">Ryd arkiv</span>
            </button>
          )}
          <button
            data-testid="btn-reset-order-numbers"
            onClick={async () => {
              if (!window.confirm("Nulstil ordrenumre, så næste bestilling bliver #1?")) return;
              try {
                await resetOrderNumbers(token);
                toast.success("Ordrenumre nulstillet – næste bestilling bliver #1");
              } catch {
                toast.error("Kunne ikke nulstille");
              }
            }}
            className="h-11 px-3 sm:px-4 rounded-xl bg-amber-100 text-amber-700 font-bold hover:bg-amber-200 active:scale-95 transition-all flex items-center gap-2"
          >
            <RotateCcw size={16} />
            <span className="hidden sm:inline">Nulstil numre</span>
          </button>
          <button
            data-testid="btn-toggle-sound"
            onClick={() => setSoundOn((v) => !v)}
            title={soundOn ? "Lyd til" : "Lyd fra"}
            className={`h-11 px-3 sm:px-4 rounded-xl font-bold transition-all active:scale-95 flex items-center gap-2 ${
              soundOn
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
          >
            {soundOn ? <Bell size={16} /> : <BellOff size={16} />}
            <span className="hidden sm:inline">{soundOn ? "Lyd til" : "Lyd fra"}</span>
          </button>
          <button
            data-testid="btn-refresh"
            onClick={() => (view === "archive" ? loadArchive() : loadOrders(token))}
            disabled={loading}
            className="h-11 px-3 sm:px-4 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 active:scale-95 transition-all flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Opdater</span>
          </button>
          <Link
            to="/"
            className="h-11 px-3 sm:px-4 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 active:scale-95 transition-all flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Butik</span>
          </Link>
          <button
            data-testid="btn-admin-logout"
            onClick={handleLogout}
            className="h-11 px-3 sm:px-4 rounded-xl bg-slate-900 text-white font-bold active:scale-95 hover:bg-slate-800 transition-all flex items-center gap-2"
          >
            <LogOut size={16} />
            <span className="hidden sm:inline">Log ud</span>
          </button>
        </div>
      </header>
      {showMessages && (
        <MessageList orders={view === "archive" ? archive.filter((o) => !o.is_deleted) : orders} onClose={() => setShowMessages(false)} />
      )}

      {/* Orders / Archive */}
      <div className="flex-1 lg:overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {view === "active" && (() => {
          const shirts = orders.reduce((n, o) => n + o.items.length, 0);
          const revenue = orders.reduce((n, o) => n + o.total, 0);
          const paid = orders
            .filter((o) => o.status === "paid" || o.status === "delivered")
            .reduce((n, o) => n + o.total, 0);
          const stats = [
            { label: "Bestillinger i alt", value: orders.length, testid: "stat-orders-today" },
            { label: "Trøjer i alt", value: shirts, testid: "stat-shirts-today" },
            { label: "Omsætning i alt", value: `${revenue} kr`, testid: "stat-revenue-today" },
            { label: "Heraf betalt", value: `${paid} kr`, testid: "stat-paid-today" },
          ];
          return (
            <div data-testid="daily-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              {stats.map((s) => (
                <div
                  key={s.testid}
                  data-testid={s.testid}
                  className="bg-white rounded-2xl border-2 border-slate-200 p-4"
                >
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                    {s.label}
                  </div>
                  <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
        {view === "active" && (
          <div className="relative mb-3">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="input-search-orders"
              type="text"
              placeholder="Søg på navn, telefonnummer eller ordrenummer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 w-full pl-11 pr-4 rounded-xl border-2 border-slate-200 bg-white text-base font-semibold focus:border-slate-900 outline-none"
            />
            {search && (
              <button
                data-testid="btn-clear-search"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 hover:text-slate-700"
              >
                Ryd
              </button>
            )}
          </div>
        )}
        {view === "active" && (
          <div data-testid="filter-bar" className="flex flex-wrap gap-2 mb-5">
            {[
              { key: "all", label: `Alle (${orders.length})` },
              { key: "pending", label: `Mangler betaling (${orders.filter((o) => o.status === "pending").length})` },
              { key: "paid", label: `Mangler udlevering (${orders.filter((o) => o.status === "paid").length})` },
              { key: "delivered", label: `Udleveret (${orders.filter((o) => o.status === "delivered").length})` },
              { key: "unpacked", label: `Ikke pakket (${orders.filter((o) => !o.packed).length})` },
            ].map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`h-10 px-4 rounded-full text-sm font-bold transition-all active:scale-95 ${
                  filter === f.key
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-100"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              data-testid="btn-print-packing"
              onClick={() => printPackingList(orders)}
              className="h-10 px-4 rounded-full text-sm font-bold transition-all active:scale-95 ml-auto flex items-center gap-1.5 bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-100"
            >
              <Printer size={16} />
              Print pakkeliste
            </button>
            <button
              data-testid="btn-toggle-sizes"
              onClick={() => setShowSizes((v) => !v)}
              className={`h-10 px-4 rounded-full text-sm font-bold transition-all active:scale-95 flex items-center gap-1.5 ${
                showSizes
                  ? "bg-amber-500 text-white"
                  : "bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-100"
              }`}
            >
              <Shirt size={16} />
              Størrelser
            </button>
            <button
              data-testid="btn-sort-name"
              onClick={() => setSortBy((s) => (s === "name" ? "date" : "name"))}
              className={`h-10 px-4 rounded-full text-sm font-bold transition-all active:scale-95 flex items-center gap-1.5 ${
                sortBy === "name"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-100"
              }`}
            >
              <ArrowDownAZ size={16} />
              {sortBy === "name" ? "Sorteret efter navn" : "Sortér efter navn"}
            </button>
          </div>
        )}
        {view === "active" && showSizes && (() => {
          const agg = {};
          orders.forEach((o) =>
            o.items.forEach((i) => {
              const p = (agg[i.product_name] = agg[i.product_name] || {});
              const key = `${i.size}|${i.color_label}`;
              p[key] = (p[key] || 0) + 1;
            })
          );
          const products = Object.keys(agg);
          return (
            <div data-testid="size-overview" className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              {products.length === 0 ? (
                <p className="text-slate-400 font-semibold col-span-full text-center py-4">
                  Ingen trøjer bestilt endnu.
                </p>
              ) : (
                products.map((name) => {
                  const entries = Object.entries(agg[name])
                    .map(([key, count]) => {
                      const [size, color] = key.split("|");
                      return { size, color, count };
                    })
                    .sort(
                      (a, b) =>
                        SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size) ||
                        a.color.localeCompare(b.color, "da")
                    );
                  const total = entries.reduce((n, e) => n + e.count, 0);
                  return (
                    <div key={name} className="bg-white rounded-2xl border-2 border-slate-200 p-4">
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="font-display text-lg font-bold text-slate-900">{name}</span>
                        <span className="text-sm font-bold text-slate-400">{total} stk</span>
                      </div>
                      <ul className="space-y-1">
                        {entries.map((e) => (
                          <li key={`${e.size}${e.color}`} className="flex justify-between text-sm bg-slate-50 rounded-lg px-3 py-1.5">
                            <span className="font-semibold text-slate-600">{e.size} · {e.color}</span>
                            <span className="font-bold text-slate-900">{e.count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}
        {view === "active" ? (
          (() => {
            const q = search.trim().toLowerCase();
            const searched = q
              ? orders.filter(
                  (o) =>
                    o.customer_name.toLowerCase().includes(q) ||
                    o.phone.includes(q) ||
                    String(o.order_number).includes(q.replace("#", ""))
                )
              : orders;
            const filtered =
              filter === "all"
                ? searched
                : filter === "unpacked"
                ? searched.filter((o) => !o.packed)
                : searched.filter((o) => o.status === filter);
            const sorted =
              sortBy === "name"
                ? [...filtered].sort(
                    (a, b) =>
                      a.customer_name.toLowerCase().localeCompare(b.customer_name.toLowerCase(), "da") ||
                      new Date(a.created_at) - new Date(b.created_at)
                  )
                : filtered;
            return sorted.length === 0 ? (
              <p className="text-center text-slate-400 font-semibold mt-12">
                {orders.length === 0
                  ? "Ingen bestillinger endnu."
                  : q
                  ? "Ingen ordrer matcher søgningen."
                  : "Ingen ordrer matcher filteret."}
              </p>
            ) : (
            <div className="space-y-4">
              {sorted.map((o) => {
                const st = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                return (
                  <div
                    key={o.id}
                    data-testid={`order-card-${o.id}`}
                    className="bg-white rounded-2xl border-2 border-slate-200 p-4 sm:p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="font-display text-xl font-bold text-slate-900">
                            #{o.order_number}
                          </span>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${st.className}`}>
                            {st.label}
                          </span>
                        </div>
                        <div className="text-slate-900 font-bold break-words">{o.customer_name}</div>
                        <div className="text-xs sm:text-sm text-slate-500 font-semibold break-words">
                          {o.phone} · {new Date(o.created_at).toLocaleString("da-DK")}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl font-bold text-slate-900 whitespace-nowrap">
                          {o.total} kr
                        </div>
                      </div>
                    </div>

                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                      {o.items.map((item, idx) => (
                        <li key={idx} className="bg-slate-50 rounded-lg p-2 text-sm">
                          <div className="font-bold text-slate-900">{item.product_name}</div>
                          <div className="text-slate-500 font-semibold">
                            {item.size} · {item.color_label} · {item.price} kr
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap gap-2">
                      {["pending", "paid", "delivered"].map((s) => (
                        <button
                          key={s}
                          data-testid={`status-${o.id}-${s}`}
                          onClick={() => handleStatus(o.id, s)}
                          className={`h-9 px-3 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                            o.status === s
                              ? "bg-slate-900 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {STATUS_LABELS[s].label}
                        </button>
                      ))}
                      <button
                        data-testid={`packed-${o.id}`}
                        onClick={() => handlePacked(o.id, !o.packed)}
                        className={`h-9 px-3 rounded-lg text-xs sm:text-sm font-bold transition-all active:scale-95 flex items-center gap-1.5 ${
                          o.packed
                            ? "bg-emerald-500 text-white"
                            : "bg-emerald-50 text-emerald-700 border-2 border-emerald-200 hover:bg-emerald-100"
                        }`}
                      >
                        <PackageCheck size={14} />
                        {o.packed ? "Pakket ✓" : "Pakket?"}
                      </button>
                      <button
                        data-testid={`print-order-${o.id}`}
                        onClick={() => printOrderSheet([o])}
                        className="h-9 px-3 rounded-lg text-xs sm:text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 active:scale-95 transition-all flex items-center gap-1.5"
                      >
                        <Printer size={14} /> Print ordre
                      </button>
                      <label
                        data-testid={`select-order-${o.id}`}
                        className={`h-9 px-3 rounded-lg text-xs sm:text-sm font-bold transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer select-none ${
                          selectedIds.has(o.id)
                            ? "bg-blue-600 text-white"
                            : "bg-blue-50 text-blue-700 border-2 border-blue-200 hover:bg-blue-100"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(o.id)}
                          onChange={() => toggleSelect(o.id)}
                          className="w-4 h-4 accent-white"
                        />
                        Print sammen
                      </label>
                      <button
                        data-testid={`delete-${o.id}`}
                        onClick={() => handleDelete(o.id)}
                        className="h-9 px-3 rounded-lg text-xs sm:text-sm font-bold bg-red-50 text-red-600 hover:bg-red-100 active:scale-95 transition-all flex items-center gap-1 ml-auto"
                      >
                        <Trash2 size={14} /> Slet
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()
        ) : archive.length === 0 ? (
          <p className="text-center text-slate-400 font-semibold mt-12">Arkivet er tomt.</p>
        ) : (
          <div className="space-y-4">
            {archive.map((o) => {
              const st = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
              return (
                <div
                  key={o.id}
                  data-testid={`archive-card-${o.id}`}
                  className={`rounded-2xl border-2 p-4 sm:p-5 ${
                    o.is_deleted
                      ? "bg-red-50/50 border-red-200"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="font-display text-xl font-bold text-slate-900">
                          #{o.order_number}
                        </span>
                        {o.is_deleted ? (
                          <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-200 text-red-900">
                            SLETTET
                          </span>
                        ) : (
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${st.className}`}>
                            {st.label}
                          </span>
                        )}
                      </div>
                      <div className="text-slate-900 font-bold break-words">{o.customer_name}</div>
                      <div className="text-xs sm:text-sm text-slate-500 font-semibold break-words">
                        {o.phone} · oprettet {new Date(o.created_at).toLocaleString("da-DK")}
                      </div>
                      {o.is_deleted && o.deleted_at && (
                        <div className="text-xs font-semibold text-red-700 mt-1">
                          Slettet {new Date(o.deleted_at).toLocaleString("da-DK")}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl font-bold text-slate-900 whitespace-nowrap">
                        {o.total} kr
                      </div>
                    </div>
                  </div>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                    {o.items.map((item, idx) => (
                      <li key={idx} className="bg-white/70 rounded-lg p-2 text-sm">
                        <div className="font-bold text-slate-900">{item.product_name}</div>
                        <div className="text-slate-500 font-semibold">
                          {item.size} · {item.color_label} · {item.price} kr
                        </div>
                      </li>
                    ))}
                  </ul>

                  {o.is_deleted && (
                    <button
                      data-testid={`restore-${o.id}`}
                      onClick={() => handleRestore(o.id)}
                      className="h-9 px-3 rounded-lg text-xs sm:text-sm font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 active:scale-95 transition-all flex items-center gap-1"
                    >
                      <Undo2 size={14} /> Gendan bestilling
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedIds.size > 0 && view === "active" && (
        <div
          data-testid="print-selected-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3"
        >
          <span className="font-bold text-sm">
            {selectedIds.size} {selectedIds.size === 1 ? "ordre valgt" : "ordrer valgt"}
          </span>
          <button
            data-testid="btn-print-selected"
            onClick={() => {
              const toPrint = orders.filter((o) => selectedIds.has(o.id));
              if (toPrint.length === 0) return;
              printOrderSheet(toPrint);
              setSelectedIds(new Set());
            }}
            className="h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 font-bold text-sm active:scale-95 transition-all flex items-center gap-1.5"
          >
            <Printer size={15} /> Print sammen på ét ark
          </button>
          <button
            data-testid="btn-clear-selected"
            onClick={() => setSelectedIds(new Set())}
            className="h-10 px-3 rounded-xl bg-slate-700 hover:bg-slate-600 font-bold text-sm active:scale-95 transition-all"
          >
            Ryd
          </button>
        </div>
      )}
    </div>
  );
}
